import { useEffect, useMemo, useState } from 'react';
import { Loader2, Trophy, FileWarning } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { FEATURES } from '@/lib/features';
import { BonusIcon } from './BonusRow';

/**
 * SAB-288 Phase 6 — Wallet bonus earnings section.
 *
 * Shows the current user's bonus award history + a YTD winnings counter.
 * Mounts inside any wallet view; designed as a self-contained section.
 *
 * The KYC threshold warning ("Approaching $5K — verify identity") is
 * wired in but gated behind FEATURES.bonus_kyc_warning, which defaults
 * false until SAB-176 ships the actual Persona verification flow. This
 * lets the visual copy live in source so the future KYC PR is a
 * one-line flag flip + destination hookup, not a new render path.
 */

interface BonusAwardRow {
  id: string;
  amount: number | string;
  awarded_for_week: number | null;
  awarded_at: string;
  reason: string | null;
  bonus: { bonus_name: string; icon: string | null } | null;
}

const KYC_THRESHOLD_USD = 5000;
const KYC_APPROACH_FRACTION = 0.9; // surface warning at 90% of threshold

function fmtUSD(n: number): string {
  return '$' + n.toLocaleString('en-US', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function formatAwardedDate(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${month} ${d.getDate()}`;
}

export function WalletBonusesSection({
  userId,
  leagueId,
  taxYear,
}: {
  userId: string;
  /** Optional league filter; omit to show bonuses across all leagues. */
  leagueId?: string;
  /** Defaults to current calendar year. */
  taxYear?: number;
}) {
  const [awards, setAwards] = useState<BonusAwardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const year = taxYear ?? new Date().getFullYear();

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      let q = supabase
        .from('bonus_awards' as any)
        .select(
          `
          id, amount, awarded_for_week, awarded_at, reason,
          bonus:league_bonuses!bonus_id(bonus_name, icon)
        `,
        )
        .eq('recipient_user_id', userId)
        .order('awarded_at', { ascending: false })
        .limit(100);
      if (leagueId) q = q.eq('league_id', leagueId);
      const { data } = await q;
      if (cancelled) return;
      setAwards(((data ?? []) as BonusAwardRow[]) ?? []);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, leagueId]);

  const ytd = useMemo(() => {
    const yearStart = new Date(year, 0, 1).getTime();
    return awards
      .filter((a) => new Date(a.awarded_at).getTime() >= yearStart)
      .reduce((sum, a) => sum + Number(a.amount), 0);
  }, [awards, year]);

  const total = useMemo(
    () => awards.reduce((sum, a) => sum + Number(a.amount), 0),
    [awards],
  );

  const approachingKyc =
    FEATURES.bonus_kyc_warning && ytd >= KYC_THRESHOLD_USD * KYC_APPROACH_FRACTION;

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 flex items-center justify-center text-muted-foreground gap-2 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading bonus history…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* YTD counter */}
      <div
        className={cn(
          'rounded-xl p-4 border',
          approachingKyc
            ? 'bg-amber-500/6 border-amber-500/40'
            : 'bg-card border-border',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div
              className={cn(
                'text-[11px] font-bold uppercase tracking-[0.06em]',
                approachingKyc ? 'text-amber-400' : 'text-muted-foreground',
              )}
            >
              YTD Winnings · {year}
            </div>
            <div className="font-bold text-2xl mt-1 tabular-nums tracking-tight">
              {fmtUSD(ytd)}
              <span className="text-xs font-medium text-muted-foreground ml-1">
                / {fmtUSD(KYC_THRESHOLD_USD)}
              </span>
            </div>
            <div className="h-1.5 mt-2 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  'h-full transition-[width] duration-300',
                  approachingKyc
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                    : 'bg-gradient-to-r from-orange-500 to-amber-500',
                )}
                style={{
                  width: `${Math.min(100, (ytd / KYC_THRESHOLD_USD) * 100)}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Wired in but gated by FEATURES.bonus_kyc_warning. SAB-176 flips the
            flag once Persona KYC has a real "Verify now" destination. */}
        {approachingKyc && (
          <div className="mt-3 pt-3 border-t border-amber-500/20 text-[11px] text-muted-foreground flex gap-2 items-start">
            <FileWarning className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
            <div className="leading-relaxed">
              Approaching $5K. IRS requires we issue a 1099-MISC and verify
              your identity.{' '}
              <span className="text-amber-400 font-semibold underline cursor-pointer">
                Verify now →
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Bonus history list */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
            Bonus History
          </div>
          {total > 0 && (
            <div className="text-[11px] text-muted-foreground tabular-nums">
              {awards.length} {awards.length === 1 ? 'award' : 'awards'} ·{' '}
              {fmtUSD(total)} all-time
            </div>
          )}
        </div>

        {awards.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
            <Trophy className="w-6 h-6 mx-auto mb-2 text-muted-foreground/50" />
            No bonuses yet. Better luck this week.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {awards.map((a, i) => {
              const icon = a.bonus?.icon ?? 'trophy';
              const name = a.bonus?.bonus_name ?? 'Bonus';
              return (
                <div
                  key={a.id}
                  className={cn(
                    'grid items-center gap-3 px-3.5 py-3',
                    'grid-cols-[32px_1fr_auto]',
                    i > 0 && 'border-t border-border/60',
                  )}
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-500/15 text-amber-400 flex items-center justify-center">
                    <BonusIcon name={icon} className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {a.awarded_for_week != null
                        ? `Wk ${a.awarded_for_week} · `
                        : ''}
                      {formatAwardedDate(a.awarded_at)}
                    </div>
                  </div>
                  <div className="font-bold text-base text-amber-400 tabular-nums">
                    +{fmtUSD(Number(a.amount))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
